/**
 * `/auth/me` returns `roleCode` as a raw permission-system identifier (`section_officer`,
 * `admin_hr`, …) — fine for authorization logic, never fine to print directly to a staff
 * member (ux-review defect: "a raw permission code is rendered to a user"). This is the one
 * place a `roleCode` is mapped to a human, bilingual label; every screen that shows a
 * person's role (`AppShell`'s account menu, the Dashboard subtitle, Account Settings) reads
 * through this function rather than rendering `roleCode` itself.
 *
 * Nepali civil-service titles (not a design choice — these are the standard translations):
 * सचिव = Secretary, सह-सचिव = Joint Secretary, उप-सचिव = Under Secretary,
 * शाखा अधिकृत = Section Officer. Note this corrects `DESIGN_SYSTEM.md` §2.2's example row,
 * which pairs "Under Secretary" with "सह-सचिव" — सह-सचिव is Joint Secretary's title, not
 * Under Secretary's; flagged in the handback rather than silently propagated.
 */
const ROLE_LABELS: Record<string, { en: string; ne: string }> = {
  super_admin: { en: 'Super Admin', ne: 'सुपर एडमिन' },
  secretary: { en: 'Secretary', ne: 'सचिव' },
  joint_secretary: { en: 'Joint Secretary', ne: 'सह-सचिव' },
  under_secretary: { en: 'Under Secretary', ne: 'उप-सचिव' },
  section_officer: { en: 'Section Officer', ne: 'शाखा अधिकृत' },
  employee: { en: 'Employee', ne: 'कर्मचारी' },
  admin_hr: { en: 'Admin / HR', ne: 'प्रशासन/मानव संसाधन' },
  auditor: { en: 'Auditor', ne: 'लेखा परीक्षक' },
};

/** Never throws, never fabricates: an unrecognised code degrades to itself, visibly honest, rather than a made-up label. */
export function roleLabel(roleCode: string | undefined | null, locale: 'en' | 'ne'): string {
  if (!roleCode) return '—';
  const entry = ROLE_LABELS[roleCode];
  if (!entry) return roleCode;
  return locale === 'ne' ? entry.ne : entry.en;
}
